import { EnvelopeIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import PublicHeader from "../components/PublicHeader";
import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { resendSignupCode, verifySignupCode } from "../services/signupService";
import { PUBLIC_BACKDROP } from "./Pricing";

const CODE_LENGTH = 6;

/** Seconds left on a countdown that started at `seconds`; 0 when done. */
function useCountdown(initial) {
  const [left, setLeft] = useState(Math.max(0, Number(initial) || 0));
  useEffect(() => {
    if (left <= 0) return undefined;
    const id = setTimeout(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [left]);
  return [left, setLeft];
}

/**
 * Sign-up step 2: the 6-digit code emailed to the owner. Reached from the
 * sign-up form (just registered, or "you already started" — 409), and from
 * login (403 email_unverified). A right code signs the owner in; the plan
 * step comes next. Resend has a 60 s cooldown and hourly caps (429).
 */
export default function VerifyEmail({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const state = useLocation().state ?? {};

  const [email, setEmail] = useState(state.email || params.get("email") || "");
  const [editingEmail, setEditingEmail] = useState(!(state.email || params.get("email")));
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [mustResend, setMustResend] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [info, setInfo] = useState(() => {
    if (state.reason === "pending") return t("verify.pendingNotice");
    if (state.reason === "unverified") return t("verify.unverifiedNotice");
    return state.email ? t("verify.sentNotice") : "";
  });
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useCountdown(state.resendIn ?? 0);
  const codeRef = useRef(null);

  useEffect(() => {
    if (!editingEmail) codeRef.current?.focus();
  }, [editingEmail]);

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== CODE_LENGTH || !email) return;
    setBusy(true);
    setError("");
    try {
      const { user } = await verifySignupCode(email.trim(), code);
      onLogin?.(user);
      navigate("/signup/plan", { replace: true });
    } catch (err) {
      const data = err.response?.data ?? {};
      if (err.response?.status === 409 && data.code === "email_already_verified") {
        setAlreadyVerified(true);
        setError(data.error);
      } else {
        setError(apiFieldErrors(err).code?.[0] ?? apiErrorMessage(err, t("verify.failed")));
        setMustResend(["code_attempts_exceeded", "code_expired"].includes(data.code));
      }
      setCode("");
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!email || resendIn > 0) return;
    setResending(true);
    setError("");
    try {
      const result = await resendSignupCode(email.trim());
      setResendIn(result.resendIn);
      setMustResend(false);
      setInfo(t("verify.resentNotice"));
      setEditingEmail(false);
    } catch (err) {
      const data = err.response?.data ?? {};
      if (err.response?.status === 429 && data.resend_available_in) setResendIn(data.resend_available_in);
      setError(apiErrorMessage(err, t("verify.resendFailed")));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen" style={PUBLIC_BACKDROP}>
      <PublicHeader />
      <main className="mx-auto w-full max-w-md px-4 pb-16 pt-4 sm:px-6 sm:pt-8">
        <div className="card p-5 sm:p-6">
          <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-accent-wash text-accent-text">
            <EnvelopeIcon className="size-6" aria-hidden="true" />
          </div>
          <h1 className="text-page-title font-bold text-ink">{t("verify.title")}</h1>
          <p className="mt-1 text-body text-ink-2">
            {email && !editingEmail ? (
              <>
                {t("verify.subtitle")}{" "}
                <span dir="ltr" className="font-semibold text-ink">
                  {email}
                </span>
              </>
            ) : (
              t("verify.subtitleNoEmail")
            )}
          </p>

          {info && !error && (
            <div
              className="mt-4 rounded-lg border border-accent-line bg-accent-wash px-3 py-2 text-sm text-ink"
              role="status"
            >
              {info}
            </div>
          )}
          {error && (
            <div
              className="mt-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text"
              role="alert"
            >
              {error}
              {alreadyVerified && (
                <>
                  {" "}
                  <Link to="/login" className="font-semibold underline">
                    {t("verify.signIn")}
                  </Link>
                </>
              )}
            </div>
          )}

          <form onSubmit={submit} className="mt-5" noValidate>
            {editingEmail && (
              <>
                <label className="field-label" htmlFor="verify_email">
                  {t("signup.ownerEmail")}
                </label>
                <input
                  id="verify_email"
                  type="email"
                  dir="ltr"
                  className="field-input mb-4"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </>
            )}

            <label className="field-label" htmlFor="verify_code">
              {t("verify.codeLabel")}
            </label>
            <input
              id="verify_code"
              ref={codeRef}
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={CODE_LENGTH}
              className={`field-input text-center font-mono text-2xl tracking-[0.5em] ${error && !alreadyVerified ? "border-danger" : ""}`}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
              placeholder="••••••"
              aria-describedby="verify_code_hint"
              disabled={mustResend || alreadyVerified}
            />
            <p id="verify_code_hint" className="mt-1 text-caption text-ink-2">
              {t("verify.codeHint")}
            </p>

            <button
              type="submit"
              className="btn-primary mt-5 w-full"
              disabled={busy || code.length !== CODE_LENGTH || !email || mustResend || alreadyVerified}
            >
              {busy ? t("verify.submitting") : t("verify.submit")}
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            <button
              type="button"
              className="font-semibold text-accent-text hover:underline disabled:cursor-not-allowed disabled:text-ink-2 disabled:no-underline"
              onClick={resend}
              disabled={resending || resendIn > 0 || !email || alreadyVerified}
            >
              {resendIn > 0
                ? t("verify.resendIn", { seconds: resendIn })
                : resending
                  ? t("verify.resending")
                  : t("verify.resend")}
            </button>
            {!editingEmail && (
              <button
                type="button"
                className="text-ink-2 hover:underline"
                onClick={() => setEditingEmail(true)}
              >
                {t("verify.otherEmail")}
              </button>
            )}
          </div>

          <p className="mt-5 border-t border-line pt-4 text-caption text-ink-2">{t("verify.spamHint")}</p>
        </div>

        <p className="mt-6 text-center text-sm text-ink-2">
          {t("pricing.haveAccount")}{" "}
          <Link to="/login" className="font-semibold text-accent-text hover:underline">
            {t("pricing.signIn")}
          </Link>
        </p>
      </main>
    </div>
  );
}
